import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, EyeOff, Flag, Heart, Loader2, MessageSquare, Trash2 } from 'lucide-react';
import { PostComunidad } from '../../types/comunidad';
import InsigniaRol from './InsigniaRol';
import HiloPost from './HiloPost';
import { tiempoRelativo } from './tiempoRelativo';

interface PostCardProps {
  post: PostComunidad;
  /** Id de quien mira: separa "mi post" del de otro. */
  usuarioId: number | undefined;
  /** PSICOLOGO/ADMIN: ven y moderan todos los posts. */
  esModerador: boolean;
  /** El post que acaba de publicar este usuario: se resalta. */
  recienPublicado?: boolean;
  liking: boolean;
  onLike: (postId: number) => void;
  onReportar: (postId: number) => void;
  onEliminar: (postId: number) => void;
  onOcultar: (postId: number) => void;
}

/**
 * Tarjeta de un post del foro.
 *
 * LO QUE ANTES NO EXISTIA
 * -----------------------
 * - Los comentarios: el boton no tenia `onClick` y el hilo vivia solo en
 *   el backend. Ahora despliega `HiloPost` bajo el post.
 * - El numero de respuestas: era `comentarios?.length` sobre los 3
 *   ultimos que traia la lista, asi que decia "3" para cualquier post
 *   con 3 o mas respuestas. Ahora sale de `_count`, que es el total real.
 * - La identidad de quien escribe: el nombre solo, y todos igual. Ahora
 *   con la insignia de rol (paciente, familia, profesional, admin).
 * - Acciones: reportar, borrar lo propio y ocultar (moderadores).
 *
 * `props` estaba declarado como `any`: con `any` nadie nota si el post
 * llega sin `_count` o con el rol cambiado.
 *
 * MÓVIL
 * -----
 * Todo lo que se toca mide 48px (`min-h-12`), el cuerpo del post va a
 * 16px —por debajo de eso un texto largo cansa en un movil— y se recorta
 * a tres lineas con "leer mas": un post entero sin recortar te echa dos
 * posts siguientes fuera de la pantalla.
 */
export default function PostCard({
  post,
  usuarioId,
  esModerador,
  recienPublicado = false,
  liking,
  onLike,
  onReportar,
  onEliminar,
  onOcultar,
}: PostCardProps) {
  const [hiloAbierto, setHiloAbierto] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [expandido, setExpandido] = useState(false);

  // El total llega del servidor (`_count`) y lo refresca el hilo al abrirlo.
  const [respuestas, setRespuestas] = useState(post._count?.comentarios ?? 0);
  useEffect(() => {
    setRespuestas(post._count?.comentarios ?? 0);
  }, [post._count?.comentarios]);

  // "¿Leer mas?" solo si el texto de verdad no cabe: se mide en vez de
  // suponerlo por longitud, porque un post de 2 lineas largas no se
  // recorta y uno de 4 palabras repetidas, si.
  const cuerpoRef = useRef<HTMLParagraphElement>(null);
  const [recortado, setRecortado] = useState(false);
  useEffect(() => {
    if (expandido) return;
    const el = cuerpoRef.current;
    if (!el) return;

    const medir = () => setRecortado(el.scrollHeight > el.clientHeight + 2);
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [post.contenido, expandido]);

  const mio = post.usuarioId === usuarioId;
  const autor = post.usuario?.nombre ?? 'Participante';

  return (
    <article
      id={`post-${post.id}`}
      aria-label={post.titulo}
      className={`scroll-mt-24 rounded-xl border bg-card p-4 transition-shadow md:scroll-mt-6 md:p-6 ${
        recienPublicado
          ? 'border-v-primary shadow-lg ring-2 ring-v-primary'
          : 'border-v-outline hover:shadow-md'
      }`}
    >
      {recienPublicado && (
        <p className="mb-3 inline-flex items-center rounded-full bg-v-primary-container px-3 py-1 text-xs font-semibold text-v-on-primary-container">
          Recién publicado
        </p>
      )}

      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-v-primary text-sm font-semibold text-white"
          >
            {autor.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-v-on-surface">
              <span className="truncate">{autor}</span>
              <InsigniaRol rol={post.usuario?.rol} />
              {mio && (
                <span className="rounded-full bg-v-background px-2 py-0.5 text-[11px] font-medium text-v-on-surface-variant">
                  Tú
                </span>
              )}
            </p>
            <p className="text-xs text-v-on-surface-variant">{tiempoRelativo(post.fecha)}</p>
          </div>
        </div>

        <span className="mt-1 shrink-0 whitespace-nowrap rounded-full bg-v-background px-2 py-1 text-xs text-v-on-surface-variant">
          {post.categoria}
        </span>
      </header>

      <h3 className="mb-1.5 text-lg font-semibold leading-snug text-v-on-surface">{post.titulo}</h3>

      <p
        ref={cuerpoRef}
        className={`whitespace-pre-wrap break-words text-base text-v-on-surface-variant ${
          // Solo en móvil: en escritorio la tarjeta es ancha y el texto
          // entero cabe, así que recortarlo ahí solo sería esconder
          // información. Como el recorte no se aplica, la medición de más
          // abajo da "no desbordado" y el botón no aparece.
          expandido ? '' : 'line-clamp-3 md:line-clamp-none'
        }`}
      >
        {post.contenido}
      </p>

      {recortado && (
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          aria-expanded={expandido}
          className="mt-1 inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-v-primary"
        >
          {expandido ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Ver menos
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Leer más
            </>
          )}
        </button>
      )}

      <footer className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-v-outline pt-1">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onLike(post.id)}
            disabled={liking}
            aria-label={`Apoyar el post de ${autor}`}
            className="no-tap-highlight flex min-h-12 items-center gap-1.5 rounded-lg px-2 text-v-on-surface-variant transition-transform active:scale-90"
          >
            {liking ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Heart className="h-5 w-5" />
            )}
            <span className="text-sm font-semibold tabular-nums">{post.likes}</span>
          </button>

          <button
            type="button"
            onClick={() => setHiloAbierto((v) => !v)}
            aria-expanded={hiloAbierto}
            aria-label={
              respuestas === 0 ? 'Responder a este post' : `Ver las ${respuestas} respuestas`
            }
            className="no-tap-highlight flex min-h-12 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold tabular-nums text-v-on-surface-variant"
          >
            <MessageSquare className="h-5 w-5" />
            {respuestas === 0 ? 'Responder' : respuestas}
          </button>
        </div>

        {!confirmandoBorrado && (
          <div className="flex items-center">
            {!mio && (
              <button
                type="button"
                onClick={() => onReportar(post.id)}
                className="no-tap-highlight flex min-h-12 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-v-on-surface-variant"
              >
                <Flag className="h-4 w-4" />
                <span className="hidden sm:inline">Reportar</span>
              </button>
            )}

            {esModerador && (
              <button
                type="button"
                onClick={() => onOcultar(post.id)}
                className="no-tap-highlight flex min-h-12 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-v-on-surface-variant"
              >
                <EyeOff className="h-4 w-4" />
                <span className="hidden sm:inline">Ocultar</span>
              </button>
            )}

            {mio && (
              <button
                type="button"
                onClick={() => setConfirmandoBorrado(true)}
                className="no-tap-highlight flex min-h-12 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-v-on-surface-variant"
              >
                <Trash2 className="h-4 w-4" />
                <span className="hidden sm:inline">Borrar</span>
              </button>
            )}
          </div>
        )}
      </footer>

      {/* La confirmacion ocupa la fila entera: en 360px, "¿Borrar? Sí No"
          al lado de los demas botones se parte en dos lineas. */}
      {confirmandoBorrado && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-v-background px-3 py-2">
          <span className="text-sm text-v-on-surface">¿Borrar este post para siempre?</span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setConfirmandoBorrado(false)}
              className="no-tap-highlight min-h-11 rounded-lg px-3 text-sm text-v-on-surface-variant"
            >
              No
            </button>
            <button
              type="button"
              onClick={() => onEliminar(post.id)}
              className="no-tap-highlight min-h-11 rounded-lg bg-v-error px-3 text-sm font-semibold text-white"
            >
              Sí
            </button>
          </span>
        </div>
      )}

      {hiloAbierto && (
        <HiloPost
          postId={post.id}
          usuarioId={usuarioId}
          esModerador={esModerador}
          onTotalCambia={setRespuestas}
        />
      )}
    </article>
  );
}
